/**
 * SequenceBadge — Compact badge showing active sequence status on lead cards.
 */

import { Workflow } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useSequenceStatus } from '@/lib/api/hooks/useProspectTimeline';

interface SequenceBadgeProps {
  prospectId: string;
}

export function SequenceBadge({ prospectId }: SequenceBadgeProps) {
  const { data } = useSequenceStatus(prospectId);
  const seq = data?.sequence;

  if (!seq) return null;

  if (seq.status === 'active') {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[11px] gap-1">
        <Workflow className="h-3 w-3" />
        Step {seq.current_step}/{seq.total_steps}
        {seq.last_step_description && ` — ${seq.last_step_description}`}
      </Badge>
    );
  }

  if (seq.status === 'completed') {
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[11px] gap-1">
        <Workflow className="h-3 w-3" />
        Sequence complete
      </Badge>
    );
  }

  if (seq.status === 'paused') {
    return (
      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-[11px] gap-1">
        <Workflow className="h-3 w-3" />
        Paused — Replied
      </Badge>
    );
  }

  if (seq.status === 'cancelled') {
    return (
      <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200 text-[11px] gap-1">
        <Workflow className="h-3 w-3" />
        Cancelled{seq.cancel_reason ? ` — ${seq.cancel_reason}` : ''}
      </Badge>
    );
  }

  return null;
}
